import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/layout/Layout';
import NewsCard from '../components/news/NewsCard';
import NewsletterForm from '../components/common/NewsletterForm';
import { newsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const allCategories = [
  { name: 'Imigração', slug: 'Imigração' },
  { name: 'Vistos', slug: 'Vistos' },
  { name: 'AIMA', slug: 'AIMA' },
  { name: 'Trabalho', slug: 'Trabalho' },
  { name: 'Custo de Vida', slug: 'Custo de Vida' },
  { name: 'Notícias', slug: 'Notícias' },
  { name: 'Curiosidades', slug: 'Curiosidades' },
];

export const CategoryPage = () => {
  const { category } = useParams();
  const decodedCategory = decodeURIComponent(category);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const limit = 9;

  const loadNews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await newsAPI.getAll({
        category: decodedCategory,
        limit: limit,
        skip: (page - 1) * limit,
      });
      setNews(response.data);
      setHasMore(response.data.length === limit);
    } catch (error) {
      console.error('Error loading news:', error);
      toast.error('Erro ao carregar notícias');
    } finally {
      setLoading(false);
    }
  }, [decodedCategory, page]);

  useEffect(() => {
    loadNews();
  }, [loadNews]);

  return (
    <Layout>
      {/* Header */}
      <section className="py-8 md:py-12 border-b border-[#D6D2C4]" data-testid="category-header">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 text-sm text-[#5C5C5C] font-['Inter'] mb-4">
            <Link to="/" className="hover:text-[#0F5A34]">Início</Link>
            <ChevronRight size={14} />
            <span className="text-[#1A1A1A]">{decodedCategory}</span>
          </div>
          
          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4">
            {decodedCategory}
          </h1>
          
          <p className="text-[#5C5C5C] font-['Inter'] text-base max-w-2xl">
            Todas as notícias sobre {decodedCategory.toLowerCase()} em Portugal. Fique atualizado com as últimas informações.
          </p>
        </div>
      </section>

      {/* Category Pills */}
      <section className="py-4 bg-white border-b border-[#D6D2C4]">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-2">
            {allCategories.map((cat) => (
              <Link key={cat.slug} to={`/categoria/${encodeURIComponent(cat.slug)}`}>
                <Badge
                  className={`px-4 py-2 font-['Montserrat'] text-xs uppercase tracking-wider cursor-pointer transition-colors ${
                    cat.slug === decodedCategory
                      ? 'bg-[#0F5A34] text-white'
                      : 'bg-transparent border border-[#D6D2C4] text-[#5C5C5C] hover:border-[#0F5A34] hover:text-[#0F5A34]'
                  }`}
                >
                  {cat.name}
                </Badge>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12" data-testid="category-content">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-8">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="spinner" />
                </div>
              ) : news.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-[#5C5C5C] font-['Inter']">
                    Nenhuma notícia encontrada nesta categoria.
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {news.map((item) => (
                      <NewsCard key={item.id} news={item} />
                    ))}
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-center gap-4 mt-8">
                    <Button
                      variant="outline"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="font-['Montserrat'] text-sm"
                      data-testid="prev-page"
                    >
                      <ChevronLeft size={16} className="mr-1" />
                      Anterior
                    </Button>
                    <span className="font-['Montserrat'] text-sm text-[#5C5C5C]">
                      Página {page}
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => setPage(p => p + 1)}
                      disabled={!hasMore}
                      className="font-['Montserrat'] text-sm"
                      data-testid="next-page"
                    >
                      Próxima
                      <ChevronRight size={16} className="ml-1" />
                    </Button>
                  </div>
                </>
              )}
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-4 space-y-6">
              <NewsletterForm variant="sidebar" />
            </aside>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <NewsletterForm />
    </Layout>
  );
};

export default CategoryPage;
